import React, { useEffect, useState } from 'react';
import '../../styles/FreshnessBadge.css';

/**
 * Small provenance badge shown on every widget:
 *   live   — pulsing dot + "4s ago" (from the last pushed metric)
 *   report — clock + "Synced 06:02" (from the dataset's last sync)
 * Turns amber when the data is older than staleAfterMs, red when there is
 * no data at all.
 */

function relative(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function syncedLabel(date, now) {
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Synced ${time}`;
  return `Synced ${date.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
}

const FreshnessBadge = ({ kind = 'live', at = null, staleAfterMs = 5 * 60 * 1000, loading = false, className = '' }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), kind === 'live' ? 5000 : 30000);
    return () => clearInterval(id);
  }, [kind]);

  const date = at ? new Date(at) : null;
  const valid = date && !Number.isNaN(date.getTime());
  const age = valid ? now - date.getTime() : null;

  let state = 'none';
  if (valid) state = age > staleAfterMs ? 'stale' : 'fresh';

  let text;
  if (loading && !valid) text = 'Loading…';
  else if (!valid) text = kind === 'live' ? 'No data' : 'Not synced';
  else if (kind === 'live') text = relative(age);
  else text = syncedLabel(date, new Date(now));

  const title = valid ? date.toLocaleString() : '';

  return (
    <span className={`freshness-badge freshness-${kind} freshness-${state} ${className}`} title={title} data-testid="freshness-badge">
      {kind === 'live' ? <span className="freshness-dot" aria-hidden="true" /> : <span className="freshness-clock" aria-hidden="true">🕒</span>}
      <span className="freshness-text">{text}</span>
    </span>
  );
};

export default FreshnessBadge;
